import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { ToastMessages } from "../../../components/toasts/toastMessages";
import { CopyIcon } from "../../../svg/copy-icon";
import { logError } from "../../../utils/customLogger";

/** Displayed values that carry no data worth putting on the clipboard. */
export const EV_COVERAGE_PLACEHOLDER_VALUES = [
  "",
  "—",
  "-",
  "--",
  "Not Available",
];

export interface EvCoverageCopyValueButtonProps {
  label: string;
  value: string;
}

/**
 * Returns whether a coverage field value should show a copy control.
 *
 * @param value - Raw or displayed field text
 * @returns False for empty/placeholder strings
 */
export const isEvCoverageValueCopyable = (value: string): boolean =>
  !EV_COVERAGE_PLACEHOLDER_VALUES.includes(value.trim());

/**
 * Copy-to-clipboard control shown beside an EV coverage field value.
 *
 * @param props.label - Field label, used for the accessible action name
 * @param props.value - Text written to the clipboard
 */
export const EvCoverageCopyValueButton = (
  props: EvCoverageCopyValueButtonProps,
) => {
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(props.value);
      toast.success(ToastMessages.KEY_COPY_SUCCESS);
    } catch (error) {
      logError(error as Error, "Error while copying to clipboard");
    }
  };

  return (
    <button
      type="button"
      aria-label={`Copy ${props.label}`}
      title={`Copy ${props.label}`}
      onClick={handleCopy}
      className="flex shrink-0 items-center transition-opacity hover:opacity-70"
    >
      <CopyIcon height="12" width="12" />
    </button>
  );
};

/**
 * Field value with the copy control placed right after the text.
 *
 * Passed as `LabeledValue`'s `value` instead of `valueIconRight`: the icon prop
 * moves the caller's `valueClassName` onto a wrapper row, which drops the field
 * typography and stretches the text away from the button.
 *
 * @param props.label - Field label, used for the accessible action name
 * @param props.value - Displayed text, also written to the clipboard
 */
export const EvCoverageValueWithCopy = (
  props: EvCoverageCopyValueButtonProps,
) => (
  <span className="flex min-w-0 items-center gap-1">
    <span className="min-w-0 truncate">{props.value}</span>
    <EvCoverageCopyValueButton label={props.label} value={props.value} />
  </span>
);
